import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';

import InputLabel from '../Inputs/InputLabel';
import { TextInputTypeV2 } from './types';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

export const TextInputV2 = React.forwardRef<HTMLInputElement, TextInputTypeV2>(
  (
    {
      id,
      label,
      placeholder = '',
      disabled,
      required = false,
      readOnly,
      shape = 'squre',
      textColorClassName = 'text-textPrimary',
      bgColorClassName = 'bg-white',
      value,
      onChange,
      onEnter,
      inputHtmlType = 'text',
      prefix,
      suffix,
      inputExtraClassNames,
      isError,
      fieldState,
      onBlur,
      span,
    },
    ref
  ) => {
    const { dir } = useCommonSelector((state) => state.common.settings);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e.target.value);
    };

    const { t } = useTranslation();

    return (
      <div className="w-full">
        {label && (
          <InputLabel
            name={id ?? ''}
            text={t(`inputs.labels.${label}`, label.toString() || '') as string}
            required={required}
            hint={span}
          />
        )}
        <div className={`relative`}>
          {prefix && (
            <div
              className={`absolute inset-y-0 ltr:left-0 rtl:right-0 ps-3 flex items-center pointer-events-none`}
            >
              <span className="text-gray-500 sm:text-sm">{prefix}</span>
            </div>
          )}
          <input
            ref={ref}
            id={id}
            type={inputHtmlType}
            disabled={disabled}
            autoComplete={readOnly ? 'off' : 'on'}
            readOnly={readOnly}
            required={required}
            value={value || ''}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={(event) => {
              if (event.code === 'Enter') {
                event.preventDefault();

                onEnter && value && onEnter(value);
              }
            }}
            className={`w-full py-1 lg:py-2 sm:text-sm border-gray-300 border ${
              !isError
                ? 'focus:border-primary focus:ring-0 focus:ring-primary'
                : 'focus:border-red-500 focus:ring-0 focus:ring-red-500'
            } ${shape === 'circle' ? 'rounded-full' : 'rounded-md'} ${
              prefix && dir === 'rtl'
                ? 'pr-7'
                : suffix && dir === 'ltr' && 'pl-7'
            } ${bgColorClassName} ${textColorClassName} ${inputExtraClassNames}`}
            placeholder={`${t(
              `inputs.placeholders.${placeholder}`,
              placeholder || ''
            )}${!label && required ? '*' : ''}`}
          />
          <div
            className={`absolute inset-y-0 ltr:right-1 rtl:left-1 flex items-center`}
          >
            {suffix}
          </div>
          {isError && (
            <div className="absolute top-[105%] right-0 text-[12px] text-red-500">
              <ReturnTranslatedText
                origintext={fieldState?.error?.message}
                text={`general.${fieldState?.error?.message}`}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);
